import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import {
  Box, Grid, Stack, Chip, Button, Link, Typography, Paper, Divider, TextField, Avatar,
} from "@mui/material";
import GlobalWrapper from "../../../components/Wrapper/GlobalWrapper";
import {
  getApplicationDetail, updateApplicationStatus, postApplicationMessage,
  getStatusOptions, createStatusOption,
} from "../../../redux/features/careerSlice";

const label = (s) => String(s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const ApplicationDetail = () => {
  const { applicationId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { applicationDetail, detailLoading, statusOptions } = useSelector((state) => state.career);
  const [msg, setMsg] = useState("");
  const [newStatus, setNewStatus] = useState(""); // custom status typed on the spot
  const [busy, setBusy] = useState(false);
  const [movingTo, setMovingTo] = useState(""); // status key currently being applied (for the button label)
  const [sending, setSending] = useState(false); // in flight state for the Send message button

  const load = () => dispatch(getApplicationDetail(applicationId));
  useEffect(() => {
    load();
    dispatch(getStatusOptions()); // the manageable status list (built-in + custom)
  }, [applicationId]); // eslint-disable-line

  // officer "move to" options = statuses meant for officers (or both)
  const moveOptions = (statusOptions || []).filter(
    (s) => s.audience === "officer" || s.audience === "both"
  );

  const d = applicationDetail || {};
  const app = d.application || {};
  const student = d.student || {};
  const history = d.history || [];
  const messages = d.messages || [];

  // proper human name for a status key: prefer the managed label, else prettify the key
  const statusLabel = (key) =>
    (statusOptions || []).find((s) => s.status_key === key)?.status_label || label(key);

  // a timeline event's title, with any raw status key swapped for its proper name.
  // Works even for deleted statuses (they drop out of the active list) by
  // prettifying the key (underscores -> spaces, title-cased).
  const eventTitle = (h) => {
    const known = (statusOptions || []).find((s) => s.status_key === h.event_type);
    const pretty = known ? known.status_label : label(h.event_type);
    const raw = h.title || label(h.event_type);
    // backend stores titles like "Status changed to got_the_call_from_firm"
    return raw.includes(h.event_type) ? raw.replace(h.event_type, pretty) : raw;
  };

  const moveTo = (status) => {
    setBusy(true);
    setMovingTo(status);
    dispatch(updateApplicationStatus({ applicationId, application_status: status }))
      .then((res) => {
        if (res.payload?.status === "success") {
          enqueueSnackbar(`Marked ${statusLabel(status)}`, { variant: "success" });
          load();
        } else {
          enqueueSnackbar(res.payload?.message || "Failed", { variant: "error" });
        }
      })
      .finally(() => { setBusy(false); setMovingTo(""); });
  };

  // type a brand-new status: create it in the managed list, then apply it here
  const addAndApply = () => {
    if (!newStatus.trim()) return;
    setBusy(true);
    dispatch(createStatusOption({ status_label: newStatus.trim() }))
      .then((res) => {
        const key = res.payload?.data?.status_key;
        if (res.payload?.status !== "success" || !key) {
          enqueueSnackbar(res.payload?.message || "Could not add status", { variant: "error" });
          setBusy(false);
          return;
        }
        setNewStatus("");
        dispatch(getStatusOptions()); // refresh the list so the new one appears
        // apply the newly-created status to this application
        dispatch(updateApplicationStatus({ applicationId, application_status: key }))
          .then((r2) => {
            if (r2.payload?.status === "success") {
              enqueueSnackbar(`Added and marked ${statusLabel(key)}`, { variant: "success" });
              load();
            } else enqueueSnackbar(r2.payload?.message || "Failed", { variant: "error" });
          })
          .finally(() => setBusy(false));
      });
  };

  const send = () => {
    if (!msg.trim()) return;
    setSending(true);
    dispatch(postApplicationMessage({ applicationId, message: msg }))
      .then((res) => {
        if (res.payload?.status === "success") { setMsg(""); load(); }
        else enqueueSnackbar(res.payload?.message || "Failed", { variant: "error" });
      })
      .finally(() => setSending(false));
  };

  return (
    <GlobalWrapper
      title={"Application Detail"}
      actions={
        <Button variant="outlined" size="small" onClick={() => navigate(-1)}>Back</Button>
      }
    >
      {detailLoading && !d.application ? (
        <Typography color="text.secondary">Loading…</Typography>
      ) : (
        <Grid container spacing={2}>
          {/* left: student + status actions */}
          <Grid item xs={12} md={5}>
            <Paper variant="outlined" sx={{ p: 2.5 }}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Avatar>{(student.student_name || "?").charAt(0)}</Avatar>
                <Box>
                  <Typography fontWeight={700}>{student.student_name || `Admn ${app.admn_no}`}</Typography>
                  <Typography variant="caption" color="text.secondary">Admn {app.admn_no}</Typography>
                </Box>
              </Stack>
              <Typography variant="body2"><b>Email:</b> {student.email || ""}</Typography>
              <Typography variant="body2"><b>Phone:</b> {student.student_mob_no || ""}</Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <b>Resume:</b>{" "}
                {app.resume_link ? <Link href={app.resume_link} target="_blank" rel="noopener">Open</Link> : ""}
              </Typography>
              {app.student_note && (
                <Typography variant="body2" sx={{ mt: 1 }}><b>Note:</b> {app.student_note}</Typography>
              )}

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2">Current status</Typography>
              <Chip color="primary" label={statusLabel(app.application_status)} sx={{ mt: 0.5, mb: 2 }} />

              <Typography variant="subtitle2" sx={{ mb: 1 }}>Move to</Typography>
              <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
                {moveOptions.map((s) => (
                  <Button
                    key={s.career_application_status_id}
                    size="small"
                    variant={app.application_status === s.status_key ? "contained" : "outlined"}
                    onClick={() => moveTo(s.status_key)}
                    disabled={busy || app.application_status === s.status_key}
                  >
                    {movingTo === s.status_key ? "Working…" : s.status_label}
                  </Button>
                ))}
              </Stack>

              {/* type a new status: it gets added to the managed list AND applied here */}
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Or add a new status</Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="e.g. Sent to HR"
                  value={newStatus}
                  inputProps={{ maxLength: 120 }}
                  onChange={(e) => setNewStatus(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addAndApply()}
                />
                <Button
                  variant="contained"
                  disabled={busy || !newStatus.trim()}
                  onClick={addAndApply}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {busy ? "Working…" : "Add & apply"}
                </Button>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                Manage all statuses under Career Connect · Statuses.
              </Typography>
            </Paper>
          </Grid>

          {/* right: timeline + messages */}
          <Grid item xs={12} md={7}>
            <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>Timeline</Typography>
              {history.length === 0 && <Typography color="text.secondary" variant="body2">No events yet.</Typography>}
              <Stack spacing={1.5}>
                {history.map((h) => (
                  <Box key={h.career_application_history_id} sx={{ borderLeft: "2px solid", borderColor: "primary.light", pl: 2 }}>
                    <Typography variant="body2" fontWeight={600}>{eventTitle(h)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {h.actor_type} · {(h.event_time || "").slice(0, 16).replace("T", " ")}
                    </Typography>
                    {h.details && <Typography variant="body2" color="text.secondary">{h.details}</Typography>}
                  </Box>
                ))}
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2.5 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>Messages</Typography>
              <Stack spacing={1} sx={{ mb: 2 }}>
                {messages.length === 0 && <Typography color="text.secondary" variant="body2">No messages yet.</Typography>}
                {messages.map((m) => (
                  <Box
                    key={m.career_message_id}
                    sx={{
                      alignSelf: m.sender_type === "officer" ? "flex-end" : "flex-start",
                      bgcolor: m.sender_type === "officer" ? "primary.main" : "grey.100",
                      color: m.sender_type === "officer" ? "#fff" : "text.primary",
                      px: 1.5, py: 1, borderRadius: 2, maxWidth: "80%",
                    }}
                  >
                    <Typography variant="body2">{m.message}</Typography>
                  </Box>
                ))}
              </Stack>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <TextField
                  size="small" fullWidth placeholder="Message the student…"
                  value={msg} onChange={(e) => setMsg(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                />
                <Button
                  variant="contained"
                  onClick={send}
                  disabled={sending || !msg.trim()}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {sending ? "Sending…" : "Send"}
                </Button>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}
    </GlobalWrapper>
  );
};

export default ApplicationDetail;
